/* ──────────────────────────────────────────────
 *  Coordonnées du client : validation partagée par la finalisation
 *  et la demande de devis, brouillon gardé le temps de la visite
 *  (créneau pris, pré-réservation expirée, retour au site).
 * ────────────────────────────────────────────── */

import { isValidPhoneNumber } from 'libphonenumber-js';
import type { CustomerInfoErrors, CustomerInfoValues } from '../components/shared/CustomerInfoFields';

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Phone is stored as E.164 from PhoneInput — validate it strictly. */
export function isValidPhone(value: string): boolean {
  try {
    return isValidPhoneNumber(value);
  } catch {
    return false;
  }
}

export function validateCustomerInfo(
  form: CustomerInfoValues,
  labels: { required_field: string; invalid_email: string; invalid_phone: string },
): CustomerInfoErrors {
  const errors: CustomerInfoErrors = {};
  if (!form.firstname.trim()) errors.firstname = labels.required_field;
  if (!form.lastname.trim()) errors.lastname = labels.required_field;
  if (!form.email.trim()) {
    errors.email = labels.required_field;
  } else if (!EMAIL_RE.test(form.email)) {
    errors.email = labels.invalid_email;
  }
  if (!form.phone.trim()) {
    errors.phone = labels.required_field;
  } else if (!isValidPhone(form.phone)) {
    errors.phone = labels.invalid_phone;
  }
  return errors;
}

export function hasCustomerInfoErrors(errors: CustomerInfoErrors): boolean {
  return Object.values(errors).some((v): boolean => !!v);
}

/** Coordonnées de la finalisation, note comprise. */
export interface CheckoutCustomerDraft extends CustomerInfoValues {
  note: string;
}

const draftKey = (slug: string): string => `cfixe_booking_customer_${slug}`;

/**
 * Brouillon des coordonnées, en `sessionStorage` : il survit au retour forcé sur les créneaux et à un aller
 * et retour sur le site pendant la visite, jamais au-delà ; effacé à la confirmation.
 */
export function readCheckoutDraft(slug: string): CheckoutCustomerDraft | undefined {
  if (!slug) return undefined;
  try {
    const raw = window.sessionStorage.getItem(draftKey(slug));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as Partial<CheckoutCustomerDraft>;
    return {
      firstname: String(parsed.firstname ?? ''),
      lastname: String(parsed.lastname ?? ''),
      email: String(parsed.email ?? ''),
      phone: String(parsed.phone ?? ''),
      note: String(parsed.note ?? ''),
    };
  } catch {
    return undefined;
  }
}

export function saveCheckoutDraft(slug: string, draft: CheckoutCustomerDraft): void {
  if (!slug) return;
  try {
    window.sessionStorage.setItem(draftKey(slug), JSON.stringify(draft));
  } catch {
    // Stockage indisponible (navigation privée, quota) : le brouillon vit seulement dans l'état du tunnel.
  }
}

export function clearCheckoutDraft(slug: string): void {
  if (!slug) return;
  try {
    window.sessionStorage.removeItem(draftKey(slug));
  } catch {
    // Rien à effacer si le stockage est indisponible.
  }
}
